import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Sparkles, X, Send, MessageCircle, Scale } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const WHATSAPP_NUMBER = "+34626125436";

const SUGGESTIONS = [
  "¿Qué es la Ley de Segunda Oportunidad?",
  "¿Puedo perder mi casa?",
  "Quiero saber si puedo cancelar mis deudas"
];

const WELCOME_MESSAGE = {
  role: 'assistant',
  content: 'Hola, soy el asistente de Perea Abogados. Sé que hablar de deudas no es fácil, pero has dado el primer paso. ¿Qué te preocupa?'
};

const ChatWidget = () => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([WELCOME_MESSAGE]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [lead, setLead] = useState(null);
  const [historyLoaded, setHistoryLoaded] = useState(false);
  const [showTooltip, setShowTooltip] = useState(true);
  const messagesEndRef = useRef(null);

  const sessionIdRef = useRef(
    (() => {
      let id = localStorage.getItem('perea_chat_session');
      if (!id) {
        id = crypto.randomUUID();
        localStorage.setItem('perea_chat_session', id);
      }
      return id;
    })()
  );

  useEffect(() => {
    if (open && !historyLoaded) {
      setHistoryLoaded(true);
      fetch(`${API}/api/chat/history/${sessionIdRef.current}`)
        .then(res => res.json())
        .then(data => {
          if (data.messages && data.messages.length > 0) {
            setMessages([WELCOME_MESSAGE, ...data.messages]);
          }
        })
        .catch(() => {});
    }
  }, [open, historyLoaded]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, loading]);

  const appendDelta = useCallback((text) => {
    setMessages(prev => {
      const copy = [...prev];
      const last = copy[copy.length - 1];
      copy[copy.length - 1] = { ...last, content: last.content + text };
      return copy;
    });
  }, []);

  const send = async (text) => {
    const message = (text ?? input).trim();
    if (!message || loading) return;

    setMessages(prev => [...prev, { role: 'user', content: message }, { role: 'assistant', content: '' }]);
    setInput('');
    setLoading(true);

    try {
      const res = await fetch(`${API}/api/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ session_id: sessionIdRef.current, message })
      });

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split('\n\n');
        buffer = parts.pop();

        for (const part of parts) {
          const line = part.trim();
          if (!line.startsWith('data:')) continue;
          try {
            const evt = JSON.parse(line.slice(5));
            if (evt.type === 'delta') {
              appendDelta(evt.text);
            } else if (evt.type === 'done') {
              if (evt.lead) setLead(evt.lead);
            } else if (evt.type === 'error') {
              appendDelta(evt.message);
            }
          } catch (e) { /* fragmento incompleto, se ignora */ }
        }
      }
    } catch (e) {
      appendDelta('Parece que hay un problema de conexión. Puedes escribirnos directamente por WhatsApp y te atendemos al momento.');
    }
    setLoading(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  const openWhatsAppWithLead = () => {
    const text = encodeURIComponent(
      `Hola, vengo del asistente de la web. Este es mi caso:\n\n${lead.resumen}\n\nDeuda aproximada: ${lead.deuda}\nSituación: ${lead.situacion}\n\nQuisiera pedir una consulta con un abogado.`
    );
    window.open(`https://wa.me/${WHATSAPP_NUMBER}?text=${text}`, '_blank');
  };

  const openWhatsAppDirect = () => {
    const text = encodeURIComponent('Hola, me gustaría recibir información sobre la Ley de Segunda Oportunidad');
    window.open(`https://wa.me/${WHATSAPP_NUMBER}?text=${text}`, '_blank');
  };

  const showChips = messages.length <= 1 && !loading;

  return (
    <div className="chat-widget">
      {open && (
        <div className="chat-panel" data-testid="chat-panel">
          <div className="chat-header">
            <div className="chat-avatar">
              <Scale size={20} />
            </div>
            <div className="chat-header-info">
              <span className="chat-title">Asistente Perea</span>
              <span className="chat-status">
                <span className="chat-status-dot"></span>
                En línea · responde al instante
              </span>
            </div>
            <button
              className="chat-header-whatsapp"
              onClick={openWhatsAppDirect}
              aria-label="Hablar por WhatsApp"
              data-testid="chat-header-whatsapp"
            >
              <MessageCircle size={18} />
            </button>
            <button
              className="chat-close"
              onClick={() => setOpen(false)}
              aria-label="Cerrar chat"
              data-testid="chat-close-button"
            >
              <X size={18} />
            </button>
          </div>

          <div className="chat-messages" data-testid="chat-messages">
            {messages.map((msg, index) => (
              msg.content && (
                <div
                  key={index}
                  className={`chat-msg ${msg.role === 'user' ? 'chat-msg-user' : 'chat-msg-assistant'}`}
                  data-testid={`chat-message-${msg.role}-${index}`}
                >
                  {msg.content}
                </div>
              )
            ))}
            {loading && messages[messages.length - 1]?.content === '' && (
              <div className="chat-msg chat-msg-assistant chat-typing" data-testid="chat-typing-indicator">
                <span></span><span></span><span></span>
              </div>
            )}
            {lead && (
              <div className="chat-lead-card" data-testid="chat-lead-card">
                <p className="chat-lead-title">Tu caso está listo para el despacho</p>
                <p className="chat-lead-summary">{lead.resumen}</p>
                <button
                  className="chat-lead-button"
                  onClick={openWhatsAppWithLead}
                  data-testid="chat-whatsapp-cta"
                >
                  <MessageCircle size={18} />
                  Enviar mi caso por WhatsApp
                </button>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {showChips && (
            <div className="chat-chips">
              {SUGGESTIONS.map((s, i) => (
                <button
                  key={i}
                  className="chat-chip"
                  onClick={() => send(s)}
                  data-testid={`chat-chip-${i}`}
                >
                  {s}
                </button>
              ))}
            </div>
          )}

          <div className="chat-input-row">
            <input
              className="chat-input"
              type="text"
              placeholder="Escribe tu duda..."
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              disabled={loading}
              data-testid="chat-input"
            />
            <button
              className="chat-send"
              onClick={() => send()}
              disabled={loading || !input.trim()}
              aria-label="Enviar mensaje"
              data-testid="chat-send-button"
            >
              <Send size={18} />
            </button>
          </div>
          <p className="chat-disclaimer">El asistente informa, pero tu caso lo estudia siempre un abogado.</p>
        </div>
      )}

      <div className="chat-launcher-row">
        <button
          className="chat-launcher"
          onClick={() => setOpen(!open)}
          aria-label="Abrir asistente virtual de Perea Abogados"
          data-testid="chat-widget-button"
        >
          {open ? <X size={26} /> : <Sparkles size={26} />}
          {!open && <span className="chat-launcher-pulse"></span>}
        </button>

        {showTooltip && !open && (
          <div className="chat-tooltip" data-testid="chat-tooltip">
            <button
              className="tooltip-close"
              onClick={() => setShowTooltip(false)}
              aria-label="Cerrar aviso"
              data-testid="chat-tooltip-close"
            >
              <X size={14} />
            </button>
            <div className="tooltip-content">
              <strong>Asistente virtual de Perea Abogados</strong>
              <p>Resuelve tus dudas sobre la Ley de Segunda Oportunidad al instante</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ChatWidget;
